import React, { useState, useEffect } from 'react'
import { Formik, Form, Field } from 'formik'
import {
	Flex,
	Box,
	Button,
	Spacer,
	Divider,
	Stack,
	VStack,
	Text,
	Table,
	Tr,
	Tbody,
	Td,
	Modal,
	ModalOverlay,
	ModalContent,
	ModalHeader,
	ModalFooter,
	ModalBody,
	ModalCloseButton,
	Grid,
	FormControl,
	Textarea,
	FormErrorMessage,
	useDisclosure,
} from '@chakra-ui/react'
import { BsFillChatDotsFill } from 'react-icons/bs'
import { format } from 'date-fns'
import { Order as OrderType } from '../types/order'
import { Message } from '../types/message'
import { CreateMessageDto } from '../dto/create-message.dto'
import { useAuth } from '../context/auth'
import { firestore } from '../lib/firebase-client'
import { getChargeText } from '../utils'

type Props = {
	children: React.ReactNode
	order: OrderType
}

export const Order = (props: Props) => {
	const { children, order } = props

	const { isOpen, onOpen, onClose } = useDisclosure()

	const { currentAdmin } = useAuth()

	const [chatId, setChatId] = useState('')
	const [messages, setMessages] = useState<Message[]>([])

	useEffect(() => {
		firestore
			.collection('chats')
			.where('shopId', '==', currentAdmin?.shop.id)
			.where('orderId', '==', order.id)
			.onSnapshot((querySnapshot) => {
				querySnapshot.forEach(async (doc) => {
					setChatId(doc.id)
				})
			})
	}, [order, currentAdmin])

	useEffect(() => {
		if (chatId) {
			firestore
				.collection('chats')
				.doc(chatId)
				.collection('messages')
				.onSnapshot((querySnapshot) => {
					const messages: Message[] = []
					querySnapshot.forEach(async (doc) => {
						messages.push({ text: doc.data().text, createdAt: doc.data().createdAt })
					})
					// 日付順にソート
					messages.sort((a, b) => {
						/* eslint-disable */
						// @ts-ignore
						return a.createdAt.toDate() - b.createdAt.toDate()
					})
					setMessages(messages)
				})
		}
	}, [chatId])

	const sendMessage = async (message: CreateMessageDto): Promise<void> => {
		if (chatId) {
			await firestore.collection('chats').doc(chatId).collection('messages').add(message)
		} else {
			const doc = await firestore.collection('chats').add({
				shopId: currentAdmin?.shop.id,
				orderId: order.id,
			})
			await doc.collection('messages').add(message)
		}
	}

	const handleDelayOrder = async (mins: number) => {
		const message = {
			text: `${mins}分遅らせたい`,
			createdAt: new Date(),
		}

		sendMessage(message)
	}

	const handleSubmit = async (values: any, actions: any) => {
		await sendMessage({
			text: values.text,
			createdAt: new Date(),
		})
		actions.resetForm()
		actions.setSubmitting(false)
	}

	const validateMessage = (value: string) => {
		let error
		if (!value) {
			error = 'メッセージを入力してください'
		}
		return error
	}

	return (
		order && (
			<>
				<Box borderWidth="1px" p={6} w={{ sm: '100%', md: '800px' }}>
					<Flex mb={6} alignItems="center">
						<Box>
							<Text>注文番号: #{order.order_group.id}</Text>
							<Text>注文種別: {order.order_group.order_type === 'delivery' ? 'デリバリー' : 'テイクアウト'}</Text>
						</Box>
						<Spacer />
						<Flex alignItems="center">
							<Text fontWeight="bold">ピックアップ時間: {format(new Date(order.pickup_at), 'MM日dd日 HH:mm')}</Text>
							<Button
								leftIcon={<BsFillChatDotsFill />}
								colorScheme="teal"
								variant="outline"
								ml={4}
								onClick={onOpen}
							>
								連絡
							</Button>
						</Flex>
					</Flex>
					<VStack divider={<Divider />} spacing={4} align="stretch" mb={8}>
						{order.line_items.map((lineItem, index) => (
							<Box key={index}>
								<Stack>
									<Flex fontSize="xl" fontWeight="bold">
										<Box>{lineItem.product.title} x {lineItem.quantity}</Box>
										<Spacer />
										<Box>&yen;{lineItem.price.toLocaleString()}</Box>
									</Flex>
									<Box fontSize="0.8rem" color="gray.500">
										{lineItem.customizations
											.map(
												(customization) =>
												customization.option.title + getChargeText(customization.price)
											)
											.join(' / ')}
									</Box>
									{lineItem.note && (
										<Box fontSize="0.8rem" color="gray.500">
											メモ: {lineItem.note}
										</Box>
									)}
								</Stack>
							</Box>
						))}
					</VStack>
					<Table variant="unstyled" size="sm" display="block" mb={6}>
						<Tbody>
							<Tr>
								<Td p={0}>合計</Td>
								<Td>&yen;{order.line_items.reduce((acc, cur) => acc + cur.price, 0).toLocaleString()}</Td>
							</Tr>
						</Tbody>
					</Table>
					{children}
				</Box>

				<Modal isOpen={isOpen} onClose={onClose}>
					<ModalOverlay />
					<ModalContent>
						<ModalHeader>注文番号: #{order.id}</ModalHeader>
						<ModalCloseButton />
						<ModalBody>
							<Box mb={4}>クイック連絡</Box>
							<Grid templateColumns="repeat(2, 1fr)" gap={6}>
								<Button colorScheme="teal" variant="outline" onClick={() => handleDelayOrder(5)}>
									5分遅らせたい
								</Button>
								<Button colorScheme="teal" variant="outline" onClick={() => handleDelayOrder(10)}>
									10分遅らせたい
								</Button>
								<Button colorScheme="teal" variant="outline" onClick={() => handleDelayOrder(20)}>
									20分遅らせたい
								</Button>
								<Button colorScheme="teal" variant="outline" onClick={() => handleDelayOrder(30)}>
									30分遅らせたい
								</Button>
							</Grid>
							{messages.length > 0 ? (
								<>
									<Divider my={8} />
									{messages.map((message, index) => (
										<Flex key={index} justifyContent="space-between">
											<Box>{message.text}</Box>
											<Box color="gray">{format(message.createdAt.toDate(), 'HH:mm')}</Box>
										</Flex>
									))}
								</>
							) : (
								<></>
							)}
							<Box mt={8}>
								<Formik initialValues={{ text: '' }} onSubmit={handleSubmit}>
									{(props) => (
										<Form>
											<Field name="text" validate={validateMessage}>
												{({ field, form }: { field: any; form: any }) => (
													<FormControl isInvalid={form.errors.text && form.touched.text}>
														<Textarea
															{...field}
															id="text"
															placeholder="本部への連絡を入力してください"
														/>
														<FormErrorMessage>{form.errors.text}</FormErrorMessage>
													</FormControl>
												)}
											</Field>
											<Button
												mt={2}
												colorScheme="teal"
												isLoading={props.isSubmitting}
												type="submit"
											>
												送信
											</Button>
										</Form>
									)}
								</Formik>
							</Box>
						</ModalBody>
						<ModalFooter>
							<></>
						</ModalFooter>
					</ModalContent>
				</Modal>
			</>
		)
	)
}
